// Phase 34 — branded 500 / page-error fallback. Keeps the dev-friendly
// collapsible stack but presents a premium, on-brand surface with Retry +
// Dashboard recovery actions.
import { Component, ErrorInfo, ReactNode } from 'react';

interface Props { children: ReactNode; }
interface State { hasError: boolean; error: Error | null; }

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error: Error): State { return { hasError: true, error }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error('ErrorBoundary caught:', error.message, info.componentStack); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-8">
          <div className="max-w-lg w-full text-center animate-fade-in">
            <div className="mx-auto mb-5 h-16 w-16 rounded-2xl bg-gradient-to-br from-red-500/20 to-amber-500/10 ring-1 ring-red-500/20 flex items-center justify-center text-3xl">
              ⚠️
            </div>
            <h2 className="text-xl font-bold">Something went wrong</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {this.state.error?.message || 'An unexpected error occurred while rendering this page.'}
            </p>
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground select-none">Technical details</summary>
              <pre className="mt-2 text-[11px] bg-card border border-border p-3 rounded-lg overflow-auto max-h-40 text-red-400/90">
                {this.state.error?.stack?.split('\n').slice(0, 6).join('\n')}
              </pre>
            </details>
            <div className="mt-6 flex gap-3 justify-center">
              <button onClick={() => this.setState({ hasError: false, error: null })} className="px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold shadow-lg shadow-primary/20 hover:-translate-y-0.5 transition-transform">Retry</button>
              <button onClick={() => (window.location.href = '/')} className="px-4 py-2.5 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent transition-colors">Go to dashboard</button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
